import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  alpha,
  Card,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell } from "recharts";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/he";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import LoginIcon from "@mui/icons-material/Login";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import BusinessIcon from "@mui/icons-material/Business";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { protectedResources } from "../../authConfig";
import { CATEGORICAL_COLORS, STATUS_COLORS, CHART_INK, formatMonthLabel } from "../../components/StatisticsDashboard/chartUtils";

const BASE_URL = (process.env.REACT_APP_BACKEND_BASE_URL || "").trim();

// Matches the "available since April 2026" notice shown on this page.
const MIN_TREND_DATA_MONTH = dayjs("2026-04-01").startOf("month");

const ACTIVITY_COLORS = {
  signIns: CATEGORICAL_COLORS[0],
  downloads: STATUS_COLORS.good,
};

const HOSPITAL_PORTAL_TYPE = "בית חולים";

const PORTAL_TYPE_COLORS = {
  "פורטל לקוחות": CATEGORICAL_COLORS[0],
  "בתי אבות": CATEGORICAL_COLORS[1],
  [HOSPITAL_PORTAL_TYPE]: CATEGORICAL_COLORS[2],
};

const HOSPITAL_CHART_COLORS = [
  CATEGORICAL_COLORS[0],
  CATEGORICAL_COLORS[1],
  CATEGORICAL_COLORS[2],
  CATEGORICAL_COLORS[3],
  CATEGORICAL_COLORS[4],
  CATEGORICAL_COLORS[5],
  CATEGORICAL_COLORS[6],
];
const HOSPITAL_OTHER_COLOR = CATEGORICAL_COLORS[7];
const MAX_HOSPITAL_SLICES = HOSPITAL_CHART_COLORS.length;

const TIME_RANGE_OPTIONS = [
  { value: "all", label: "הכל" },
  { value: "day", label: "24 שעות" },
  { value: "week", label: "שבוע" },
  { value: "month", label: "חודש" },
];

const HospitalActivityTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const entry = payload[0].payload;
  return (
    <Box sx={{ bgcolor: "background.paper", p: 1.5, borderRadius: 1, boxShadow: 3, maxWidth: 260 }}>
      <Typography variant="body2" fontWeight={700}>{entry.name} — {entry.count} התראות</Typography>
      {entry.others?.length > 0 && (
        <Box mt={0.5} display="flex" flexDirection="column" gap={0.25}>
          {entry.others.map((o) => (
            <Typography key={o.hospitalName} variant="caption" color="text.secondary">
              {o.hospitalName}: {o.count} התראות
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const getTimeRangeSinceIso = (range) => {
  if (range === "all") return null;
  const since = new Date();
  if (range === "day") since.setHours(since.getHours() - 24);
  else if (range === "week") since.setDate(since.getDate() - 7);
  else if (range === "month") since.setMonth(since.getMonth() - 1);
  return since.toISOString();
};

const toIsoDate = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const to = new Date();
  const from = new Date(to.getFullYear(), to.getMonth(), 1);
  return { fromDate: toIsoDate(from), toDate: toIsoDate(to) };
};

const formatDateDMY = (isoDate) => {
  if (!isoDate) return "";
  const [year, month, day] = isoDate.split("-");
  return `${day}/${month}/${year}`;
};

const formatTrendLabel = (label, granularity) => {
  if (granularity === "month") {
    const [year, month] = label.split("-").map(Number);
    return formatMonthLabel(year, month);
  }
  const [year, month, day] = label.split("-").map(Number);
  return new Date(year, month - 1, day).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit" });
};

const StatCard = ({ label, value, icon: Icon, color }) => (
  <Card
    sx={{
      p: 2.5,
      borderRadius: 3,
      display: "flex",
      alignItems: "center",
      gap: 2,
      height: "100%",
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "50%",
        bgcolor: alpha(color, 0.12),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ color, fontSize: 26 }} />
    </Box>
    <Box>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Box>
  </Card>
);

const CustomerPortalPage = () => {
  const [{ fromDate, toDate }, setDateRange] = useState(getDefaultDateRange());
  const [summary, setSummary] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [companiesTimeRange, setCompaniesTimeRange] = useState("all");
  const [activitySummary, setActivitySummary] = useState(null);
  const [activityPortalType, setActivityPortalType] = useState("");
  const [activityTimeRange, setActivityTimeRange] = useState("all");
  const [signInsTrend, setSignInsTrend] = useState([]);
  const [signInsGranularity, setSignInsGranularity] = useState("day");
  const [trendMonthAnchor, setTrendMonthAnchor] = useState(() => dayjs().startOf("month"));
  const [trendYearAnchor, setTrendYearAnchor] = useState(() => dayjs().startOf("year"));

  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingHospitals, setLoadingHospitals] = useState(true);
  const [loadingActivitySummary, setLoadingActivitySummary] = useState(true);
  const [loadingTrend, setLoadingTrend] = useState(true);
  const [error, setError] = useState(null);

  const { execute: fetchRead } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const params = new URLSearchParams();
      const since = getTimeRangeSinceIso(companiesTimeRange);
      if (since) params.set("createdAfter", since);

      const res = await fetchRead("GET", `${BASE_URL}/api/customerPortal/users?${params.toString()}`);
      setCompanies(res.data || []);
    } catch (err) {
      console.error("Error loading customer portal companies:", err);
      setError(err.message || "שגיאה בטעינת רשימת הלקוחות");
    } finally {
      setLoadingCompanies(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesTimeRange]);

  const loadHospitalActivity = useCallback(async () => {
    setLoadingHospitals(true);
    try {
      const params = new URLSearchParams();
      const since = getTimeRangeSinceIso(companiesTimeRange);
      if (since) params.set("since", since);

      const res = await fetchRead("GET", `${BASE_URL}/api/customerPortal/hospitalsActivity?${params.toString()}`);
      setHospitals(res.data || []);
    } catch (err) {
      console.error("Error loading customer portal hospital activity:", err);
      setError(err.message || "שגיאה בטעינת נתוני בתי החולים");
    } finally {
      setLoadingHospitals(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companiesTimeRange]);

  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const params = new URLSearchParams({ startDate: fromDate, endDate: toDate }).toString();
      const res = await fetchRead("GET", `${BASE_URL}/api/customerPortal/summary?${params}`);
      setSummary(res);
    } catch (err) {
      console.error("Error loading customer portal summary:", err);
      setError(err.message || "שגיאה בטעינת נתוני הסיכום");
    } finally {
      setLoadingSummary(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const loadActivitySummary = useCallback(async () => {
    setLoadingActivitySummary(true);
    try {
      const params = new URLSearchParams();
      const since = getTimeRangeSinceIso(activityTimeRange);
      if (since) {
        params.set("startDate", since);
        params.set("endDate", new Date().toISOString());
      }
      if (activityPortalType) params.set("portalType", activityPortalType);

      const res = await fetchRead("GET", `${BASE_URL}/api/customerPortal/summary?${params.toString()}`);
      setActivitySummary(res);
    } catch (err) {
      console.error("Error loading customer portal activity summary:", err);
      setError(err.message || "שגיאה בטעינת יומן הפעילות");
    } finally {
      setLoadingActivitySummary(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityPortalType, activityTimeRange]);

  const trendDateRange = useMemo(() => {
    const today = dayjs();
    if (signInsGranularity === "month") {
      const start = trendYearAnchor.startOf("year");
      const end = trendYearAnchor.isSame(today, "year") ? today : trendYearAnchor.endOf("year");
      return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
    }
    const start = trendMonthAnchor.startOf("month");
    const end = trendMonthAnchor.isSame(today, "month") ? today : trendMonthAnchor.endOf("month");
    return { startDate: start.format("YYYY-MM-DD"), endDate: end.format("YYYY-MM-DD") };
  }, [signInsGranularity, trendMonthAnchor, trendYearAnchor]);

  const loadSignInsTrend = useCallback(async () => {
    setLoadingTrend(true);
    try {
      const params = new URLSearchParams({
        startDate: trendDateRange.startDate,
        endDate: trendDateRange.endDate,
        granularity: signInsGranularity,
      }).toString();
      const res = await fetchRead("GET", `${BASE_URL}/api/customerPortal/signInsTrend?${params}`);
      setSignInsTrend(res.data || []);
    } catch (err) {
      console.error("Error loading customer portal sign-ins trend:", err);
      setError(err.message || "שגיאה בטעינת מגמת ההתחברויות");
    } finally {
      setLoadingTrend(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendDateRange, signInsGranularity]);

  const handleTrendPrev = () => {
    if (signInsGranularity === "month") {
      setTrendYearAnchor((prev) => prev.subtract(1, "year"));
    } else {
      setTrendMonthAnchor((prev) => prev.subtract(1, "month"));
    }
  };

  const handleTrendNext = () => {
    if (signInsGranularity === "month") {
      setTrendYearAnchor((prev) => prev.add(1, "year"));
    } else {
      setTrendMonthAnchor((prev) => prev.add(1, "month"));
    }
  };

  const isTrendPrevDisabled = signInsGranularity === "month"
    ? !trendYearAnchor.isAfter(MIN_TREND_DATA_MONTH, "year")
    : !trendMonthAnchor.isAfter(MIN_TREND_DATA_MONTH, "month");

  const isTrendNextDisabled = signInsGranularity === "month"
    ? trendYearAnchor.isSame(dayjs(), "year")
    : trendMonthAnchor.isSame(dayjs(), "month");

  const trendPeriodLabel = signInsGranularity === "month"
    ? trendYearAnchor.format("YYYY")
    : trendMonthAnchor.locale("he").format("MMMM YYYY");

  useEffect(() => { loadCompanies(); }, [loadCompanies]);
  useEffect(() => { loadHospitalActivity(); }, [loadHospitalActivity]);
  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadActivitySummary(); }, [loadActivitySummary]);
  useEffect(() => { loadSignInsTrend(); }, [loadSignInsTrend]);

  const portalTypeOptions = useMemo(
    () => Array.from(new Set(companies.map((c) => c.portalType).filter(Boolean))),
    [companies]
  );

  const portalTypeBreakdown = useMemo(() => {
    const counts = companies.reduce((acc, c) => {
      const type = c.portalType || "לא מוגדר";
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    const total = companies.length;
    return Object.entries(counts).map(([type, count]) => ({
      type,
      count,
      percent: total ? Math.round((count / total) * 1000) / 10 : 0,
    }));
  }, [companies]);

  const totalCustomersCount = companies.length;

  const hospitalActivityBreakdown = useMemo(() => {
    const sorted = [...hospitals].sort((a, b) => b.count - a.count);
    const top = sorted.slice(0, MAX_HOSPITAL_SLICES);
    const rest = sorted.slice(MAX_HOSPITAL_SLICES);
    const restCount = rest.reduce((sum, h) => sum + h.count, 0);
    const entries = restCount > 0 ? [...top, { hospitalName: "אחר", count: restCount, others: rest }] : top;
    const total = entries.reduce((sum, h) => sum + h.count, 0);
    return entries.map((h, index) => ({
      name: h.hospitalName,
      count: h.count,
      percent: total ? Math.round((h.count / total) * 1000) / 10 : 0,
      color: h.hospitalName === "אחר" ? HOSPITAL_OTHER_COLOR : HOSPITAL_CHART_COLORS[index % HOSPITAL_CHART_COLORS.length],
      others: h.others,
    }));
  }, [hospitals]);

  const trendChartData = useMemo(
    () => signInsTrend.map((point) => ({ label: formatTrendLabel(point.label, signInsGranularity), count: point.count })),
    [signInsTrend, signInsGranularity]
  );

  const activityBreakdown = useMemo(() => {
    const signIns = activitySummary?.totalSignIns ?? 0;
    const downloads = activitySummary?.totalDownloadsSuccess ?? 0;
    const total = signIns + downloads;
    return [
      { key: "signIns", label: "התחברויות", count: signIns, percent: total ? Math.round((signIns / total) * 1000) / 10 : 0 },
      { key: "downloads", label: "הורדות קבצים", count: downloads, percent: total ? Math.round((downloads / total) * 1000) / 10 : 0 },
    ];
  }, [activitySummary]);

  const pageColor = CATEGORICAL_COLORS[6];

  return (
    <Box display="flex" flexDirection="column" gap={3} p={2} bgcolor="#fff" minHeight="100vh">
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "50%",
            bgcolor: alpha(pageColor, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <PeopleAltIcon sx={{ color: pageColor, fontSize: 26 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700}>פורטל לקוחות</Typography>
          <Typography variant="body2" color="text.secondary">
            פעילות לקוחות בפורטל הלקוחות — התחברויות, הורדות קבצים ושגיאות
          </Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ borderRadius: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      <Alert severity="info" sx={{ borderRadius: 3 }}>
        שימו לב: הנתונים המוצגים במסך זה זמינים החל מחודש אפריל 2026.
      </Alert>

      <Card sx={{ p: 2, borderRadius: 3, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="he">
          <DatePicker
            label="מתאריך"
            format="DD/MM/YYYY"
            value={dayjs(fromDate)}
            onChange={(newValue) => {
              if (newValue && newValue.isValid()) {
                setDateRange((prev) => ({ ...prev, fromDate: newValue.format("YYYY-MM-DD") }));
              }
            }}
            slotProps={{ textField: { size: "small" } }}
          />
          <DatePicker
            label="עד תאריך"
            format="DD/MM/YYYY"
            value={dayjs(toDate)}
            onChange={(newValue) => {
              if (newValue && newValue.isValid()) {
                setDateRange((prev) => ({ ...prev, toDate: newValue.format("YYYY-MM-DD") }));
              }
            }}
            slotProps={{ textField: { size: "small" } }}
          />
        </LocalizationProvider>
      </Card>

      <Typography variant="body2" color="text.secondary">
        מציג נתונים מתאריך {formatDateDMY(fromDate)} עד {formatDateDMY(toDate)}
      </Typography>

      {loadingSummary ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: pageColor }} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="התחברויות" value={summary?.totalSignIns ?? 0} icon={LoginIcon} color={CATEGORICAL_COLORS[0]} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="הורדות מוצלחות" value={summary?.totalDownloadsSuccess ?? 0} icon={CloudDoneIcon} color={STATUS_COLORS.good} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="שגיאות והורדות שנכשלו" value={summary?.totalErrors ?? 0} icon={ErrorOutlineIcon} color={STATUS_COLORS.critical} />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="לקוחות פעילים" value={`${summary?.activeCompanies ?? 0} / ${summary?.totalCompanies ?? 0}`} icon={BusinessIcon} color={CATEGORICAL_COLORS[6]} />
          </Grid>
        </Grid>
      )}

      <Divider />

      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={1.5}>
          <Typography variant="h6" fontWeight={700}>מגמת התחברויות</Typography>
          <Box display="flex" alignItems="center" gap={2} flexWrap={{ xs: "wrap", sm: "nowrap" }}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <IconButton size="small" onClick={handleTrendPrev} disabled={isTrendPrevDisabled} aria-label="תקופה קודמת">
                <NavigateNextIcon fontSize="small" />
              </IconButton>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 90, textAlign: "center" }}>
                {trendPeriodLabel}
              </Typography>
              <IconButton size="small" onClick={handleTrendNext} disabled={isTrendNextDisabled} aria-label="תקופה הבאה">
                <NavigateBeforeIcon fontSize="small" />
              </IconButton>
            </Box>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={signInsGranularity}
              onChange={(_, value) => value && setSignInsGranularity(value)}
            >
              <ToggleButton value="day">יומי</ToggleButton>
              <ToggleButton value="month">חודשי</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Card sx={{ p: 2, borderRadius: 3 }}>
          {loadingTrend ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: pageColor }} /></Box>
          ) : trendChartData.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני התחברויות לטווח שנבחר</Typography>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trendChartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="signInsTrendGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CATEGORICAL_COLORS[0]} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={CATEGORICAL_COLORS[0]} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={CHART_INK.grid} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={{ stroke: CHART_INK.axis }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: CHART_INK.muted }} axisLine={false} tickLine={false} width={36} allowDecimals={false} />
                <Tooltip formatter={(value) => [`${value} התחברויות`, ""]} cursor={{ stroke: CHART_INK.axis, strokeWidth: 1 }} />
                <Area
                  type="monotone"
                  dataKey="count"
                  name="התחברויות"
                  stroke={CATEGORICAL_COLORS[0]}
                  strokeWidth={2}
                  fill="url(#signInsTrendGradient)"
                  dot={{ r: 3, fill: CATEGORICAL_COLORS[0], strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>
      </Box>

      <Divider />

      <Grid container spacing={2} alignItems="stretch">
      <Grid item xs={12} md={4} sx={{ display: "flex" }}>
      <Box sx={{ width: "100%", display: "flex" }}>
        <Card sx={{ p: 2, borderRadius: 3, width: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>לקוחות הפורטל לפי סוג</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={companiesTimeRange}
              onChange={(_, value) => value && setCompaniesTimeRange(value)}
            >
              {TIME_RANGE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            הנתונים המוצגים הם סה״כ מצטבר לתקופה הנבחרת
          </Typography>
          {loadingCompanies || loadingHospitals ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: pageColor }} /></Box>
          ) : totalCustomersCount === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני לקוחות</Typography>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} p={1} sx={{ flexGrow: 1 }}>
              <Box sx={{ position: "relative", width: 220, height: 220, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={portalTypeBreakdown}
                      dataKey="count"
                      nameKey="type"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {portalTypeBreakdown.map((entry) => (
                        <Cell key={entry.type} fill={PORTAL_TYPE_COLORS[entry.type] || CATEGORICAL_COLORS[3]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} לקוחות`, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="h5" fontWeight={700}>{totalCustomersCount}</Typography>
                  <Typography variant="caption" color="text.secondary">סה״כ לקוחות</Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {portalTypeBreakdown.map((entry) => (
                  <Box key={entry.type} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: PORTAL_TYPE_COLORS[entry.type] || CATEGORICAL_COLORS[3],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2">
                      <strong>{entry.type}</strong> — {entry.percent}% ({entry.count} לקוחות)
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: "flex" }}>
      <Box sx={{ width: "100%", display: "flex" }}>
        <Card sx={{ p: 2, borderRadius: 3, width: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>בתי חולים הפעילים ביותר</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={companiesTimeRange}
              onChange={(_, value) => value && setCompaniesTimeRange(value)}
            >
              {TIME_RANGE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            אחוז ההתראות שנשלחו לכל בית חולים מתוך סך ההתראות
          </Typography>
          {loadingHospitals ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: pageColor }} /></Box>
          ) : hospitalActivityBreakdown.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני בתי חולים</Typography>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} p={1} sx={{ flexGrow: 1 }}>
              <Box sx={{ position: "relative", width: 220, height: 220, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={hospitalActivityBreakdown}
                      dataKey="count"
                      nameKey="name"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {hospitalActivityBreakdown.map((entry) => (
                        <Cell key={entry.name} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<HospitalActivityTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="h5" fontWeight={700}>{hospitals.length}</Typography>
                  <Typography variant="caption" color="text.secondary">בתי חולים</Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {hospitalActivityBreakdown.map((entry) => {
                  const row = (
                    <Box display="flex" alignItems="center" gap={1.5} sx={{ cursor: entry.others?.length ? "help" : "default" }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: entry.color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="body2">
                        <strong>{entry.name}</strong> — {entry.percent}% ({entry.count} התראות)
                      </Typography>
                    </Box>
                  );
                  if (!entry.others?.length) return <React.Fragment key={entry.name}>{row}</React.Fragment>;
                  return (
                    <MuiTooltip
                      key={entry.name}
                      arrow
                      title={
                        <Box display="flex" flexDirection="column" gap={0.25}>
                          {entry.others.map((o) => (
                            <Typography key={o.hospitalName} variant="caption">
                              {o.hospitalName}: {o.count} התראות
                            </Typography>
                          ))}
                        </Box>
                      }
                    >
                      {row}
                    </MuiTooltip>
                  );
                })}
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      </Grid>

      <Grid item xs={12} md={4} sx={{ display: "flex" }}>
      <Box sx={{ width: "100%", display: "flex" }}>
        <Card sx={{ p: 2, borderRadius: 3, width: "100%", display: "flex", flexDirection: "column" }}>
          <Box sx={{ mb: 0.5 }}>
            <Typography variant="h6" fontWeight={700} mb={1}>יומן פעילות</Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activityTimeRange}
                onChange={(_, value) => value && setActivityTimeRange(value)}
              >
                {TIME_RANGE_OPTIONS.map((option) => (
                  <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                ))}
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activityPortalType}
                onChange={(_, value) => value !== null && setActivityPortalType(value)}
              >
                <ToggleButton value="">הכל</ToggleButton>
                {portalTypeOptions.map((option) => (
                  <ToggleButton key={option} value={option}>{option}</ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            הנתונים המוצגים הם סה״כ מצטבר לתקופה הנבחרת
          </Typography>
          {loadingActivitySummary ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress sx={{ color: pageColor }} /></Box>
          ) : activityBreakdown.every((entry) => entry.count === 0) ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>אין נתוני פעילות לטווח שנבחר</Typography>
          ) : (
            <Box display="flex" flexDirection="column" alignItems="flex-start" gap={2} p={1} sx={{ flexGrow: 1 }}>
              <Box sx={{ position: "relative", width: 220, height: 220, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={activityBreakdown}
                      dataKey="count"
                      nameKey="label"
                      innerRadius={70}
                      outerRadius={100}
                      paddingAngle={2}
                      stroke="none"
                    >
                      {activityBreakdown.map((entry) => (
                        <Cell key={entry.key} fill={ACTIVITY_COLORS[entry.key]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [value, name]} />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    textAlign: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Typography variant="h5" fontWeight={700}>
                    {activityBreakdown.reduce((sum, entry) => sum + entry.count, 0)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">סה״כ פעולות</Typography>
                </Box>
              </Box>
              <Box display="flex" flexDirection="column" gap={1.5}>
                {activityBreakdown.map((entry) => (
                  <Box key={entry.key} display="flex" alignItems="center" gap={1.5}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: ACTIVITY_COLORS[entry.key],
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2">
                      <strong>{entry.label}</strong> — {entry.percent}% ({entry.count})
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Card>
      </Box>
      </Grid>
      </Grid>
    </Box>
  );
};

export default CustomerPortalPage;
